import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { StoreModuleTypes } from '@/constants/store-constants';
import store from '@/store';

const STORAGE_KEYS = {
    TASK: 'crm_task_defaults',
    MEETING: 'crm_meeting_defaults'
};
const SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

interface StorageItem {
    value: any;
    timestamp: number;
}

function isSessionValid(timestamp: number): boolean {
    const now = Date.now();
    return (now - timestamp) < SESSION_TIMEOUT;
}

function saveToStorage(key: string, value: any) {
    try {
        const storageItem: StorageItem = {
            value: value,
            timestamp: Date.now()
        };
        sessionStorage.setItem(key, JSON.stringify(storageItem));
    } catch {
        // Fail silently if storage is not available
    }
}

function loadFromStorage(key: string) {
    try {
        const item = sessionStorage.getItem(key);
        if (!item) return null;
        const storageItem: StorageItem = JSON.parse(item);
        if (!isSessionValid(storageItem.timestamp)) {
            sessionStorage.removeItem(key);
            return null;
        }
        return storageItem.value;
    } catch {
        return null;
    }
}

export interface TaskReminderDefaults {
    staffEmailEnabled: boolean;
    staffEmailAmount: number | null;
    staffEmailUnit: string | null;
    staffTextEnabled: boolean;
    staffTextAmount: number | null;
    staffTextUnit: string | null;
}

export interface MeetingReminderDefaults extends TaskReminderDefaults {
    guardianEmailEnabled: boolean;
    guardianEmailAmount: number | null;
    guardianEmailUnit: string | null;
    guardianTextEnabled: boolean;
    guardianTextAmount: number | null;
    guardianTextUnit: string | null;
    addToCalendar: boolean;
}

export interface TaskDefaultsState {
    task: TaskReminderDefaults | null;
    meeting: MeetingReminderDefaults | null;
}

@Module({
    namespaced: true,
    name: StoreModuleTypes.TASK_DEFAULTS,
    dynamic: true,
    store: store
})
export class TaskDefaultsStore extends VuexModule implements TaskDefaultsState {
    task: TaskReminderDefaults | null = loadFromStorage(STORAGE_KEYS.TASK);
    meeting: MeetingReminderDefaults | null = loadFromStorage(STORAGE_KEYS.MEETING);

    @Mutation
    setTaskDefaults(defaults: TaskReminderDefaults) {
        const existingDefaults = loadFromStorage(STORAGE_KEYS.TASK);
        const newDefaults = existingDefaults ? {
            ...existingDefaults,
            ...defaults
        } : defaults;
        this.task = newDefaults;
        saveToStorage(STORAGE_KEYS.TASK, newDefaults);
    }

    @Mutation
    setMeetingDefaults(defaults: MeetingReminderDefaults) {
        const existingDefaults = loadFromStorage(STORAGE_KEYS.MEETING);
        const newDefaults = existingDefaults ? {
            ...existingDefaults,
            ...defaults
        } : defaults;
        this.meeting = newDefaults;
        saveToStorage(STORAGE_KEYS.MEETING, newDefaults);
    }

    @Mutation
    clearDefaults() {
        sessionStorage.removeItem(STORAGE_KEYS.TASK);
        sessionStorage.removeItem(STORAGE_KEYS.MEETING);
        this.task = null;
        this.meeting = null;
    }
}
